import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getRpcIncidents } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const duration = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`);

const Row = ({ start, end, minutes, ongoing }) => (
  <li data-testid="incident-row" className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#050505] border border-[#2A2411]">
    <div className="flex items-center gap-3 min-w-0">
      <span className={`h-2 w-2 rounded-full shrink-0 ${ongoing ? "bg-red-500 animate-pulse" : "bg-zinc-500"}`} />
      <div className="min-w-0">
        <p className="text-sm text-white">{ongoing ? "RPC outage in progress" : "RPC outage"}</p>
        <p className="text-[11px] text-zinc-500 font-mono">{when(start)}{end ? ` → ${when(end)}` : " → now"}</p>
      </div>
    </div>
    <span data-testid="incident-duration" className={`text-xs font-semibold font-mono shrink-0 ${ongoing ? "text-red-400" : "text-zinc-300"}`}>
      {ongoing ? "ongoing" : duration(minutes)}
    </span>
  </li>
);

export const IncidentHistory = () => {
  const { data } = useQuery({ queryKey: ["rpcIncidents"], queryFn: getRpcIncidents, refetchInterval: 60000 });
  if (!data) return null;
  const empty = !data.ongoing && data.incidents.length === 0;
  return (
    <Card data-testid="status-incidents" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <AlertTriangle size={16} className="text-[#D4AF37]" /> Incident History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p data-testid="incidents-empty" className="flex items-center gap-2 text-sm text-zinc-400">
            <ShieldCheck size={16} className="text-green-400" /> No RPC outages recorded. Outages of 5+ minutes appear here.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.ongoing && <Row start={data.ongoing.start} ongoing />}
            {data.incidents.map((i) => <Row key={i.start} {...i} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
